import * as Yup from 'yup-updated';

import type { InferType } from 'yup-updated';

const bankAccountSchema = Yup.object().shape({
  bankAccountId: Yup.string().required(),
  bankAccountToken: Yup.string().required(),
  accountMask: Yup.string().required(),
  updatedOnUtc: Yup.string()
});

const cardSchema = Yup.object().shape({
  cardId: Yup.string().required(),
  cardMask: Yup.string().required(),
  cardBrand: Yup.string().required(),
  updatedOnUtc: Yup.string()
});

export type BankAccount = InferType<typeof bankAccountSchema>;
export type Card = InferType<typeof cardSchema>;
export type DigitalWallet = 'ApplePay' | 'GooglePay';

const paymentSelectionBankAccountSchema = Yup.object().shape({
  bankAccountId: Yup.string().required(),
  bankAccountToken: Yup.string().required(),
  accountMask: Yup.string().required(),
  routingNumber: Yup.string().required()
});

const paymentSelectionCardSchema = Yup.object().shape({
  cardId: Yup.string().required(),
  fingerprint: Yup.string().required(),
  cardType: Yup.string().required(),
  cardMask: Yup.string().required(),
  cardBrand: Yup.string().required(),
  digitalWallet: Yup.string()
});

const paymentSelectionSchema = Yup.object().shape({
  selectionType: Yup.string().required(),
  selectedOnUtc: Yup.string().required(),
  bankAccount: paymentSelectionBankAccountSchema.optional().default(undefined),
  card: paymentSelectionCardSchema.optional().default(undefined),
  digitalWallet: Yup.string().optional().default(undefined)
});

export type PaymentSelectionBankAccount = InferType<typeof paymentSelectionBankAccountSchema>;
export type PaymentSelectionCard = InferType<typeof paymentSelectionCardSchema>;
export type PaymentSelection = InferType<typeof paymentSelectionSchema>;

export const paymentMethodSummarySchema = Yup.object()
  .shape({
    paymentType: Yup.string().required(),
    purchasePaymentBreakdown: Yup.object()
      .shape({
        cashDown: Yup.number().required(),
        shippingFee: Yup.number().required(),
        creditFromCustomerAccountBalance: Yup.number().required(),
        credit: Yup.number().required(),
        amountDue: Yup.number().required(),
        refundDue: Yup.number().required()
      })
      .required(),
    paymentsOnPurchase: Yup.array()
      .of(
        Yup.object()
          .shape({
            paymentId: Yup.string().required(),
            amount: Yup.number().required(),
            status: Yup.string().required(),
            paymentMethod: Yup.string().required(),
            createdOnUtc: Yup.date().required(),
            description: Yup.string(),
            failureCode: Yup.string()
          })
          .required()
      )
      .required(),
    paymentSelection: paymentSelectionSchema.optional().default(undefined),
    defaultBankAccount: bankAccountSchema.optional().default(undefined),
    defaultCard: cardSchema.optional().default(undefined),
    isCardEligible: Yup.boolean().required(),
    buyerFirstName: Yup.string().required(),
    buyerLastName: Yup.string().required(),
    coSignerFirstName: Yup.string(),
    coSignerLastName: Yup.string(),
    customerEmail: Yup.string(),
    customerPhoneNumber: Yup.string()
  })
  .required();

export type PaymentMethodSummary = InferType<typeof paymentMethodSummarySchema>;

export const createStripeSetupIntentResponseSchema = Yup.object().shape({
  setupIntentId: Yup.string().required(),
  clientSecret: Yup.string().required()
});

export interface AddBankAccountRequest {
  accountNumber: string;
  routingNumber: string;
  bankAccountType: 'Checking' | 'Savings';
}

export interface AddBankAccountFromPlaidRequest {
  plaidAccountId: string;
  plaidPublicToken: string;
}

export interface AddCardFromStripeRequest {
  setupIntentId: string;
}

export interface SelectBankAccountRequest {
  bankAccountId: string;
}

export interface SelectCardRequest {
  cardId: string;
}

export interface SelectDigitalWalletRequest {
  digitalWallet?: DigitalWallet;
}

export const addAccountErrorResponseSchema = Yup.object().shape({
  customerErrorMessage: Yup.string().optional()
});

export const plaidLinkTokenResponseSchema = Yup.object().shape({
  linkToken: Yup.string().required()
});

export { bankAccountSchema, cardSchema };
